import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import {
  type AdjustmentLineFormValue,
  AdjustmentLineRow,
} from '../../../components/bookkeeping/AdjustmentLineRow.js';
import type { Item, Warehouse } from '../../../types/bookkeeping.js';

const REASONS = ['opening', 'count', 'damage', 'theft', 'other'] as const;

function defaultLine(): AdjustmentLineFormValue {
  return { item_id: '', qty_delta: '1000', unit_cost: '0', description: '' };
}

export function AdjustmentForm() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const { activeOrgId } = useAuth();

  const today = new Date().toISOString().slice(0, 10);

  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState<(typeof REASONS)[number]>('count');
  const [entryDate, setEntryDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<AdjustmentLineFormValue[]>([defaultLine()]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: warehousesData } = useQuery({
    queryKey: ['bk-warehouses', activeOrgId],
    queryFn: () => api<{ warehouses: Warehouse[] }>('/bookkeeping/inventory/warehouses'),
    enabled: !!activeOrgId,
  });
  const warehouses = (warehousesData?.warehouses ?? []).filter((w) => w.is_active);

  const { data: itemsData } = useQuery({
    queryKey: ['bookkeeping-items', activeOrgId],
    queryFn: () => api<{ items: Item[] }>('/bookkeeping/items'),
    enabled: !!activeOrgId,
  });
  const items = itemsData?.items ?? [];

  const submitMutation = useMutation({
    mutationFn: () =>
      api<{ adjustment: { id: string } }>('/bookkeeping/inventory/adjustments', {
        method: 'POST',
        body: JSON.stringify({
          warehouse_id: warehouseId,
          reason,
          entry_date: entryDate,
          memo: memo || null,
          lines: lines.map((l, idx) => ({
            line_number: idx + 1,
            item_id: l.item_id,
            qty_delta: l.qty_delta,
            unit_cost: l.unit_cost,
            description: l.description || null,
          })),
        }),
      }),
    onSuccess: () => {
      void nav('/app/bookkeeping/inventory/adjustments');
    },
    onError: (err: Error) => {
      setSubmitError(err.message ?? 'unknown_error');
    },
  });

  function updateLine(index: number, value: AdjustmentLineFormValue) {
    setLines((prev) => prev.map((l, i) => (i === index ? value : l)));
  }

  function removeLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  function addLine() {
    setLines((prev) => [...prev, defaultLine()]);
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">{t('inventory.adjustments.newTitle')}</h1>

      {/* Header */}
      <div className="grid grid-cols-2 gap-4 rounded border bg-white p-4 md:grid-cols-4">
        <label className="block">
          <span className="text-sm">{t('inventory.adjustments.warehouse')} *</span>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
          >
            <option value="">—</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm">{t('inventory.adjustments.reason')} *</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as (typeof REASONS)[number])}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
          >
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`inventory.adjustments.reasons.${r}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm">{t('inventory.adjustments.entry_date')} *</span>
          <input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-sm">{t('inventory.adjustments.memo')}</span>
          <input
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder={t('inventory.adjustments.memo_placeholder')}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
          />
        </label>
      </div>

      {/* Lines */}
      <div className="rounded border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('inventory.adjustments.item')}</th>
                <th className="px-2 py-2">{t('inventory.adjustments.qty_delta')}</th>
                <th className="px-2 py-2">{t('inventory.adjustments.unit_cost')}</th>
                <th className="px-2 py-2">{t('inventory.adjustments.description')}</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <AdjustmentLineRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: form rows have no stable id; idx is fine for unmounted-only ordered list
                  key={idx}
                  index={idx}
                  value={line}
                  items={items}
                  canRemove={lines.length > 1}
                  onChange={(v) => updateLine(idx, v)}
                  onRemove={() => removeLine(idx)}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t p-2">
          <button type="button" onClick={addLine} className="text-sm text-blue-600 hover:underline">
            + {t('inventory.adjustments.add_line')}
          </button>
        </div>
      </div>

      {submitError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {t(`inventory.errors.${submitError}`, { defaultValue: submitError })}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending || !warehouseId}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {t('inventory.adjustments.submit')}
        </button>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/inventory/adjustments')}
          className="rounded border px-4 py-2 text-sm"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
